// Runtime config read from window.environment (set by /config.js).
// /config.js is served by nginx from a Helm-rendered ConfigMap in deployed
// environments, and from public/config.js in local dev. Fallbacks below
// mirror .env.development so the app still boots if window.environment is
// missing or partial.
const win =
	typeof window !== "undefined" && (window as any).environment
		? ((window as any).environment as Record<string, unknown>)
		: {};

// Coerce any incoming value to a string so downstream string comparisons
// (e.g. `=== "true"`) keep working even if a Helm author writes the value
// unquoted in YAML and it lands as a JS boolean / number.
//   undefined | null  → fallback
//   true              → "true"
//   false             → "false"
//   42                → "42"
//   "foo"             → "foo"
const asString = (raw: unknown, fallback: string): string =>
	raw === undefined || raw === null ? fallback : String(raw);

export const environment = {
	REACT_APP_API_URL: asString(win.REACT_APP_API_URL, "https://kural.dev.zsservices.com/api/"),
	REACT_APP_BASE_PATH: asString(win.REACT_APP_BASE_PATH, "/"),
	REACT_APP_AZURE_CLIENT_ID: asString(win.REACT_APP_AZURE_CLIENT_ID, ""),
	REACT_APP_AZURE_TENANT_ID: asString(win.REACT_APP_AZURE_TENANT_ID, ""),
	REACT_APP_AZURE_API_SCOPE: asString(win.REACT_APP_AZURE_API_SCOPE, ""),
	// Single authoritative IdP selector: "IDM" | "COGNITO" | "ENTRA" (read
	// case-insensitively in loginMethods.ts). Fails closed to IDM.
	REACT_APP_IDP_TYPE: asString(win.REACT_APP_IDP_TYPE, "IDM"),
	REACT_APP_SIGNOUT_URL: asString(win.REACT_APP_SIGNOUT_URL, ""),
	REACT_APP_REDIRECT_URL: asString(win.REACT_APP_REDIRECT_URL, ""),
	REACT_APP_REDIRECT_PAGE_URL: asString(win.REACT_APP_REDIRECT_PAGE_URL, ""),
	REACT_APP_ADD_USER_URL: asString(win.REACT_APP_ADD_USER_URL, ""),
	REACT_APP_ENTRA_IDENTITY_ENABLED: asString(win.REACT_APP_ENTRA_IDENTITY_ENABLED, "false"),
	REACT_APP_THEME_NAME: asString(win.REACT_APP_THEME_NAME, "Kural"),
	REACT_APP_INSTANCE_NAME: asString(win.REACT_APP_INSTANCE_NAME, "Kural"),
	REACT_APP_CLIENT_NAME: asString(win.REACT_APP_CLIENT_NAME, ""),
	REACT_APP_LOGIN_CLIENT_NAME: asString(win.REACT_APP_LOGIN_CLIENT_NAME, ""),
	REACT_APP_DEMO_PROFILE: asString(win.REACT_APP_DEMO_PROFILE, "Demo"),
	REACT_APP_ALLOWED_DOMAINS: asString(win.REACT_APP_ALLOWED_DOMAINS, "zs.com,zsservices.com"),
	REACT_APP_ZS_DOMAINS: asString(win.REACT_APP_ZS_DOMAINS, "zs.com,zsservices.com"),
	REACT_APP_GATEWAY_ENABLED: asString(win.REACT_APP_GATEWAY_ENABLED, "true"),
	REACT_APP_RELEASE_NOTE_VERSION: asString(win.REACT_APP_RELEASE_NOTE_VERSION, ""),
	REACT_APP_DOCUMENT_TITLE: asString(win.REACT_APP_DOCUMENT_TITLE, "Kural"),
	REACT_APP_MONITORING_AVAILABLE: asString(win.REACT_APP_MONITORING_AVAILABLE, "false"),
	REACT_APP_PARTNERSHIP_LOGO_NAME: asString(win.REACT_APP_PARTNERSHIP_LOGO_NAME, ""),
	REACT_APP_FAVICON_NAME: asString(win.REACT_APP_FAVICON_NAME, "Kural"),

	// Legal links surfaced on auth-adjacent pages (e.g. the post-logout
	// SignedOut screen). Optional — the UI only renders a link when its value
	// is present, so leaving these empty simply hides the corresponding link.
	REACT_APP_PRIVACY_POLICY_URL: asString(win.REACT_APP_PRIVACY_POLICY_URL, ""),
	REACT_APP_TERMS_URL: asString(win.REACT_APP_TERMS_URL, ""),

	// Login / auth. The active IdP is chosen by the single REACT_APP_IDP_TYPE
	// var above (`idm` | `cognito` | `entra`) — exactly one per deployment,
	// mirroring the gateway's IDP_PROVIDER. See public/config.template.js.
	// IMPORTANT: REACT_APP_IDP_TYPE FAILS CLOSED to "IDM" (see its fallback
	// above). If window.environment is missing/partial (e.g. config.js failed to
	// mount in a deployed env) we must never silently log everyone into a
	// hardcoded Cognito pool (wrong tenant / wrong IdP, violates NFR-1's
	// "one pool per tenant, never shared"). Cognito is opt-in via IDP_TYPE.
	// The four COGNITO_* values below are only consumed when IDP_TYPE=cognito.
	REACT_APP_COGNITO_USER_POOL_ID: asString(win.REACT_APP_COGNITO_USER_POOL_ID, ""),
	REACT_APP_COGNITO_CLIENT_ID: asString(win.REACT_APP_COGNITO_CLIENT_ID, ""),
	REACT_APP_COGNITO_DOMAIN: asString(win.REACT_APP_COGNITO_DOMAIN, ""),
	REACT_APP_COGNITO_REGION: asString(win.REACT_APP_COGNITO_REGION, ""),

	// Cognito federated-IdP names (the `custom:` provider passed to
	// signInWithRedirect), one per login button. Configurable per tenant so the
	// IdP wired into the User Pool can change without a code change. Defaults
	// match the current testing setup.
	REACT_APP_COGNITO_ZS_PROVIDER: asString(win.REACT_APP_COGNITO_ZS_PROVIDER, "ZS-Internal"),
	REACT_APP_COGNITO_CLIENT_PROVIDER: asString(win.REACT_APP_COGNITO_CLIENT_PROVIDER, "ZS-Entra-Dev"),
};

// Case-insensitive boolean-ish check. Use this instead of `=== "true"` when
// reading values that come from helm/config.js, so misconfigurations like
// `REACT_APP_GATEWAY_ENABLED: True` or `REACT_APP_MONITORING_AVAILABLE: 1`
// don't silently behave as false.
export const isTruthy = (value: string | undefined | null): boolean => {
	if (!value) return false;
	const v = value.toLowerCase();
	return v === "true" || v === "1" || v === "yes" || v === "on";
};
